import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState, useCallback } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import Image from '@tiptap/extension-image';
import type { Editor } from '@tiptap/react';
import { ingestImageFiles, imageFilesOf, isPastableImage, nameForPastedImage } from '../../mail/utils/inlineImageIngest';
import { compressImageForEmail } from '../../mail/utils/imageCompress';
import './TerminalRichText.scss';

/**
 * TerminalRichText — purpose-built rich-text editor for the terminal
 * composer. Headless Tiptap under the hood (ProseMirror engine) so we
 * own the entire UI. No CKEditor.
 *
 * What's in the box:
 *   - Bold / Italic / Underline / Strike / Code
 *   - Bulleted + ordered lists, blockquote, horizontal rule
 *   - Links with an inline URL popover (no browser prompt())
 *   - Undo / Redo
 *   - Markdown shortcuts enabled while typing:
 *       **bold**  _italic_  `code`  ~~strike~~
 *       -  or  *   → bullet list
 *       1.          → ordered list
 *       >           → blockquote
 *       ---         → horizontal rule
 *   - Auto-link of typed URLs (http://..., https://..., mailto:...)
 *   - Placeholder text when empty
 *   - HTML in / HTML out via value + onChange
 *
 * The host component (TerminalSendEmailForm) wraps this with chrome —
 * quoted-reply block, recipient chips, attachments, send/save — and
 * never touches the editor's internal state directly.
 */

// ── Toolbar button primitive ─────────────────────────────────────────────
// Bracketed text, active-aware. [B] bright green when bold is active at
// cursor, dim when not. Same family as the workspace's other action
// chrome so the editor doesn't feel visually imported.
interface ToolBtnProps {
  label: React.ReactNode;
  active?: boolean;
  disabled?: boolean;
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  title: string;
  /** Extra class on the button (e.g. to exempt it from outside-dismissal). */
  extraClass?: string;
}
const ToolBtn: React.FC<ToolBtnProps> = ({ label, active, disabled, onClick, title, extraClass }) => (
  <button
    type="button"
    className={
      'term-rt__tool'
      + (active ? ' term-rt__tool--active' : '')
      + (disabled ? ' term-rt__tool--disabled' : '')
      + (extraClass ? ` ${extraClass}` : '')
    }
    disabled={disabled}
    onClick={(e) => { e.preventDefault(); onClick(e); }}
    onMouseDown={(e) => e.preventDefault() /* keep editor focus */}
    title={title}
  >
    <span className="term-rt__tool-bracket">[</span>
    <span className="term-rt__tool-label">{label}</span>
    <span className="term-rt__tool-bracket">]</span>
  </button>
);

// ── Inline link popover ──────────────────────────────────────────────────
// Sits under the toolbar when open. Two inputs: url + (optional) text.
// If there's a selection, the selection becomes the link text and we
// skip the text input. Enter commits, Escape cancels.
interface LinkPopoverProps {
  editor: Editor;
  onClose: () => void;
}
const LinkPopover: React.FC<LinkPopoverProps> = ({ editor, onClose }) => {
  // Prefill from an existing link at the cursor (so "edit link" works
  // naturally when the cursor is inside an existing link).
  const currentLink = editor.getAttributes('link')?.href || '';
  const [url, setUrl] = useState<string>(currentLink);
  const inputRef = useRef<HTMLInputElement>(null);

  // autoFocus on the input handles focus (applied during insertion, which
  // iOS honors with the on-screen keyboard — a useEffect focus() runs
  // outside the tap gesture and iOS silently ignores it). This effect only
  // pre-selects an existing URL for easy replacement.
  useEffect(() => {
    inputRef.current?.select();
  }, []);

  const commit = () => {
    const trimmed = url.trim();
    if (!trimmed) {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      onClose();
      return;
    }
    // Normalize: if user typed "foo.com" without a scheme, prepend https://
    const normalized = /^[a-z]+:/i.test(trimmed) ? trimmed : `https://${trimmed}`;
    editor.chain().focus().extendMarkRange('link').setLink({ href: normalized }).run();
    onClose();
  };

  const remove = () => {
    editor.chain().focus().extendMarkRange('link').unsetLink().run();
    onClose();
  };

  return (
    <div className="term-rt__link-popover" onMouseDown={(e) => e.stopPropagation()}>
      <span className="term-rt__link-label">url:</span>
      <input
        ref={inputRef}
        type="text"
        inputMode="url"
        enterKeyHint="go"
        autoFocus
        className="term-rt__link-input"
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter')  { e.preventDefault(); commit(); }
          if (e.key === 'Escape') { e.preventDefault(); onClose(); }
        }}
        placeholder="https://example.com"
        spellCheck={false}
        autoCorrect="off"
        autoCapitalize="off"
      />
      <button
        type="button"
        className="term-rt__link-action"
        onMouseDown={(e) => e.preventDefault()}
        onClick={commit}
        title="Apply link (Enter)"
      >
        <span className="term-rt__tool-bracket">[</span>ok<span className="term-rt__tool-bracket">]</span>
      </button>
      {currentLink && (
        <button
          type="button"
          className="term-rt__link-action term-rt__link-action--danger"
          onMouseDown={(e) => e.preventDefault()}
          onClick={remove}
          title="Remove link"
        >
          <span className="term-rt__tool-bracket">[</span>remove<span className="term-rt__tool-bracket">]</span>
        </button>
      )}
      <button
        type="button"
        className="term-rt__link-action"
        onMouseDown={(e) => e.preventDefault()}
        onClick={onClose}
        title="Cancel (Esc)"
      >
        <span className="term-rt__tool-bracket">[</span>×<span className="term-rt__tool-bracket">]</span>
      </button>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────

/**
 * Where a snippet may be inserted so it lands in the part of the message the
 * owner wrote. The composer's send guard (and its "unresolved variable" note)
 * only inspects the HTML BEFORE the first `<hr>` - the anchor the quoted reply /
 * forward thread starts with. A snippet dropped while the caret sits below that
 * rule would carry its `{{variables}}` into a region nobody checks, and the
 * client would receive the literal placeholder. So a position past the first
 * horizontal rule is clamped to just above it; a position at or before it is
 * returned untouched.
 *
 * When the rule is nested (inside a blockquote / list item) the clamp goes to
 * the boundary before its top-level ancestor, which is always a valid place to
 * insert blocks and still serialises before the `<hr>`.
 */
export function ownerRegionInsertPos(doc: Editor['state']['doc'], pos: number): number {
  let hrPos = -1;
  doc.descendants((node, nodePos) => {
    if (hrPos >= 0) return false;
    if (node.type.name === 'horizontalRule') { hrPos = nodePos; return false; }
    return true;
  });
  if (hrPos < 0 || pos <= hrPos) return pos;
  const $hr = doc.resolve(hrPos);
  return $hr.depth > 0 ? $hr.before(1) : hrPos;
}

/**
 * What the host may ask of the editor imperatively. Everything else flows
 * through value / onChange.
 */
export interface TerminalRichTextHandle {
  /**
   * Insert HTML at the caret (snippets). A selection is collapsed to its END
   * first, so nothing the owner wrote is ever replaced; the change goes through
   * the normal transaction path, so onChange fires and autosave picks it up.
   * A caret sitting in the quoted thread (below the first horizontal rule) is
   * moved up to just above that rule - see `ownerRegionInsertPos`.
   * `focus: false` (phones) keeps the on-screen keyboard from popping up.
   * Returns false when nothing was inserted (editor not ready / read-only).
   */
  insertHtmlAtCursor: (html: string, opts?: { focus?: boolean }) => boolean;
  /**
   * Move every picture still embedded as a base64 `data:` URL (a forward's re-embedded images, a draft from
   * before pictures were uploaded, a paste whose upload failed) out to Cloud Storage and point the <img> at the
   * stored copy. The caret and the rest of the document are untouched, so it is safe to run while the owner is
   * writing. This is what lets a draft that is "over the 1 MB save limit" shrink and save. Resolves how many were
   * moved / could not be. No-op (0/0) without an `uploadImage` prop.
   */
  offloadInlineImages: () => Promise<{ moved: number; failed: number }>;
}

export interface TerminalRichTextProps {
  /** HTML content (controlled). */
  value: string;
  /** Fires on every document change with the latest HTML. */
  onChange: (html: string) => void;
  /** Placeholder text when editor is empty. */
  placeholder?: string;
  /** Editable or read-only. */
  editable?: boolean;
  /** Extra class on the outer wrapper. */
  className?: string;
  /** Optional minimum editor-body height (px). Defaults to 120. */
  minBodyHeight?: number;
  /**
   * Where a pasted or dropped picture goes. When given, the picture is compressed, uploaded to Cloud Storage and
   * inserted as a small <img src="<download url>">, so it never bloats the draft document (one Firestore doc,
   * 1 MiB cap). Absent: the picture is embedded as a base64 data: URL (the original behaviour).
   */
  uploadImage?: (blob: Blob, name: string) => Promise<{ src: string; bytes?: number }>;
  /** How many picture uploads are running right now - the host holds Send while this is above 0. */
  onImageUploadsChange?: (pending: number) => void;
  /** A picture that could not be pasted (too big, unreadable), with the reason to show the owner. */
  onImageRejected?: (reason: string) => void;
}

const readAsDataUrl = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });

const TerminalRichText = forwardRef<TerminalRichTextHandle, TerminalRichTextProps>(({
  value,
  onChange,
  placeholder = 'write your reply…',
  editable = true,
  className,
  minBodyHeight = 120,
  uploadImage,
  onImageUploadsChange,
  onImageRejected,
}, ref) => {
  const [linkOpen, setLinkOpen] = useState(false);

  // The editor's paste / drop handlers are created once, so they read the latest props through refs.
  const uploadImageRef = useRef(uploadImage);
  uploadImageRef.current = uploadImage;
  const onUploadsChangeRef = useRef(onImageUploadsChange);
  onUploadsChangeRef.current = onImageUploadsChange;
  const onRejectedRef = useRef(onImageRejected);
  onRejectedRef.current = onImageRejected;
  const pendingUploadsRef = useRef(0);
  const bumpPending = (delta: 1 | -1) => {
    pendingUploadsRef.current = Math.max(0, pendingUploadsRef.current + delta);
    onUploadsChangeRef.current?.(pendingUploadsRef.current);
  };
  const warnImage = (message: string, err?: unknown) => {
    // eslint-disable-next-line no-console
    console.warn(`[TerminalRichText] ${message}:`, (err as Error)?.message || err || '');
  };

  // Dismiss the link popover on tap/click outside it. The advertised cancel
  // path is Escape, which doesn't exist on phone keyboards — without this,
  // touch users' only out was the tiny [×] glyph.
  useEffect(() => {
    if (!linkOpen) return;
    const onDown = (e: Event) => {
      const target = e.target as HTMLElement | null;
      if (target?.closest('.term-rt__link-popover')) return;
      // The [link] tool itself is exempt — dismissing on its pointerdown made
      // its onClick re-mount the popover fresh, discarding any typed URL.
      if (target?.closest('.term-rt__tool--link')) return;
      setLinkOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [linkOpen]);

  /**
   * Compress, upload and insert pictures (paste or drop). With an `uploadImage` prop they go to Cloud Storage and
   * the body only holds a URL; without one (or if an upload fails) they are embedded as data: URLs so a picture is
   * never lost. See utils/inlineImageIngest.
   */
  const insertPictures = async (files: File[], atPos?: number) => {
    if (typeof atPos === 'number' && editor?.isEditable) {
      try { editor.chain().focus().setTextSelection(atPos).run(); } catch { /* caret stays where it was */ }
    }
    await ingestImageFiles(files, {
      compress: async (file, name) => {
        const r = await compressImageForEmail(file, name);
        return { blob: r.blob, name: r.name };
      },
      upload: uploadImageRef.current,
      shouldUpload: (blob) => isPastableImage(blob.type),
      toDataUrl: readAsDataUrl,
      // Chain into the editor; `isEditable` is re-checked per picture because the
      // host may have locked the editor mid-upload (e.g. a send started).
      insert: (src) => { editor?.chain().focus().setImage({ src }).run(); },
      isEditable: () => !!editor?.isEditable,
      onPending: bumpPending,
      onReject: (reason) => onRejectedRef.current?.(reason),
      warn: warnImage,
    });
  };

  const editor = useEditor({
    editable,
    extensions: [
      // Starter-kit bundles: Document, Paragraph, Text, Bold, Italic,
      // Strike, Code, Heading, BulletList, OrderedList, ListItem,
      // Blockquote, HorizontalRule, HardBreak, History, Dropcursor,
      // Gapcursor. Markdown input rules are enabled by default.
      StarterKit.configure({
        // Headings de-emphasized for email — we keep the extension but
        // it's mostly unused; starter defaults are fine.
        heading: { levels: [1, 2, 3] },
        // Starter includes its own Link extension disabled by default —
        // configure separately below so we own open-in-tab behavior.
      }),
      Underline,
      Link.configure({
        openOnClick: false,          // links open when user clicks OUR [link] action, not inside the editor
        autolink: true,              // type http://foo → auto-becomes a link
        linkOnPaste: true,           // paste a URL onto selected text → link
        defaultProtocol: 'https',
        protocols: ['http', 'https', 'mailto', 'tel'],
        HTMLAttributes: {
          target: '_blank',
          rel: 'noopener noreferrer nofollow',
        },
      }),
      Placeholder.configure({
        placeholder,
        emptyEditorClass: 'term-rt__body--empty',
      }),
      // Inline images. A pasted picture is compressed and uploaded to Cloud
      // Storage and inserted as a small <img src="<download url>"> (see
      // handlePaste) - a base64 data: URL inside the HTML made a draft
      // exceed Firestore's 1 MiB document cap. The server turns the stored
      // pictures into inline (cid:) attachments at send. data: URLs are still
      // allowed (forwards re-embed small ones; a failed upload falls back to
      // one). `inline: false` keeps images as block nodes so they break
      // text flow naturally.
      Image.configure({
        allowBase64: true,
        inline: false,
        HTMLAttributes: { class: 'term-rt__image' },
      }),
    ],
    content: value || '',
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      // ── Paste: insert clipboard images inline at cursor ─────────────
      //
      // Default ProseMirror paste handling for clipboard images yields
      // a blob-URL `<img src="blob:...">` that evaporates on reload.
      // Instead: read the File, encode as base64, and insert an Image
      // node with the data URL — so the image is part of the HTML
      // payload and persists through draft save / send.
      //
      // Non-image clipboards (text / html) fall through untouched so
      // normal copy-paste keeps working.
      handlePaste(view, event) {
        const items = event.clipboardData?.items;
        if (!items || items.length === 0) return false;

        const imageFiles: File[] = [];
        for (let i = 0; i < items.length; i++) {
          const item = items[i];
          if (item.kind === 'file' && item.type.startsWith('image/')) {
            const f = item.getAsFile();
            if (f) imageFiles.push(f);
          }
        }
        if (imageFiles.length === 0) return false;

        // Async compress → upload → insert. We return `true` immediately to
        // stop the default HTML/blob paste; the pictures land one after
        // another, in the order pasted, as each finishes.
        void insertPictures(imageFiles);
        return true;
      },

      // ── Drop: image files dragged onto the editor go the same way ────
      handleDrop(view, event) {
        const files = imageFilesOf(event.dataTransfer?.files);
        if (files.length === 0) return false;
        event.preventDefault();
        const at = view.posAtCoords({ left: event.clientX, top: event.clientY });
        void insertPictures(files, at ? at.pos : undefined);
        return true;
      },
    },
  });

  // Sync external value changes into the editor (without nuking cursor
  // position when the value === the editor's current content).
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if (value !== current) {
      // `emitUpdate: false` prevents a feedback loop via onUpdate.
      editor.commands.setContent(value || '', { emitUpdate: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  // Keep the editable flag in sync if toggled by the host.
  useEffect(() => {
    if (editor && editor.isEditable !== editable) editor.setEditable(editable);
  }, [editor, editable]);

  // Keyboard shortcut: Ctrl/Cmd+K to open the link popover with whatever
  // is currently selected.
  const handleEditorKeyDown = useCallback((e: React.KeyboardEvent) => {
    const isModK = (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k';
    if (isModK) {
      e.preventDefault();
      setLinkOpen(true);
    }
  }, []);

  useImperativeHandle(ref, () => ({
    insertHtmlAtCursor: (html, opts) => {
      if (!editor || !editor.isEditable || !html) return false;
      try {
        // insertContentAt(position) with from === to never replaces anything; the
        // selection END is the position so a selected run of text survives. It is
        // clamped above the quote rule so the send guard can always see the text.
        const { doc, selection } = editor.state;
        const at = ownerRegionInsertPos(doc, selection.to);
        const chain = opts?.focus === false ? editor.chain() : editor.chain().focus();
        return chain.insertContentAt(at, html).run();
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[TerminalRichText] insertHtmlAtCursor failed:', (err as Error)?.name || 'error');
        return false;
      }
    },

    offloadInlineImages: async () => {
      const upload = uploadImageRef.current;
      if (!editor || !editor.isEditable || !upload) return { moved: 0, failed: 0 };
      // Distinct data: sources in the document right now.
      const sources: string[] = [];
      editor.state.doc.descendants((node) => {
        const src = node.type.name === 'image' ? String(node.attrs?.src || '') : '';
        if (/^data:image\//i.test(src) && sources.indexOf(src) < 0) sources.push(src);
        return true;
      });
      if (sources.length === 0) return { moved: 0, failed: 0 };

      const stored = new Map<string, string>();
      let failed = 0;
      bumpPending(1);
      try {
        for (let i = 0; i < sources.length; i++) {
          try {
            const blob = await (await fetch(sources[i])).blob();
            const small = await compressImageForEmail(blob, nameForPastedImage(blob, i));
            stored.set(sources[i], (await upload(small.blob, small.name)).src);
          } catch (err) {
            failed += 1;
            warnImage('could not move an embedded picture to storage', err);
          }
        }
      } finally {
        bumpPending(-1);
      }

      if (stored.size > 0) {
        // Re-find each picture BY ITS SOURCE at apply time (the owner may have typed during the uploads, so
        // positions captured earlier could have moved) and only swap the src: same node size, caret untouched.
        editor.chain().command(({ tr }) => {
          tr.doc.descendants((node, pos) => {
            if (node.type.name !== 'image') return true;
            const to = stored.get(String(node.attrs?.src || ''));
            if (to) tr.setNodeMarkup(pos, undefined, { ...node.attrs, src: to });
            return true;
          });
          return true;
        }).run();
      }
      return { moved: stored.size, failed };
    },
  }), [editor]);

  if (!editor) return null;

  return (
    <div className={'term-rt' + (className ? ` ${className}` : '')}>
      {/* ── Toolbar ───────────────────────────────────────────────── */}
      <div className="term-rt__toolbar" role="toolbar" aria-label="Rich text formatting">
        <ToolBtn
          label={<b>B</b>}
          active={editor.isActive('bold')}
          title="Bold (Ctrl+B)"
          onClick={() => editor.chain().focus().toggleBold().run()}
        />
        <ToolBtn
          label={<i>I</i>}
          active={editor.isActive('italic')}
          title="Italic (Ctrl+I)"
          onClick={() => editor.chain().focus().toggleItalic().run()}
        />
        <ToolBtn
          label={<u>U</u>}
          active={editor.isActive('underline')}
          title="Underline (Ctrl+U)"
          onClick={() => editor.chain().focus().toggleUnderline().run()}
        />
        <ToolBtn
          label={<s>S</s>}
          active={editor.isActive('strike')}
          title="Strikethrough"
          onClick={() => editor.chain().focus().toggleStrike().run()}
        />
        <span className="term-rt__toolbar-sep" aria-hidden="true">│</span>
        <ToolBtn
          label="· list"
          active={editor.isActive('bulletList')}
          title="Bulleted list"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        />
        <ToolBtn
          label="1. list"
          active={editor.isActive('orderedList')}
          title="Numbered list"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        />
        <ToolBtn
          label="❝ quote"
          active={editor.isActive('blockquote')}
          title="Blockquote"
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
        />
        <ToolBtn
          label="code"
          active={editor.isActive('code')}
          title="Inline code"
          onClick={() => editor.chain().focus().toggleCode().run()}
        />
        <span className="term-rt__toolbar-sep" aria-hidden="true">│</span>
        <ToolBtn
          label="link"
          active={editor.isActive('link')}
          title="Insert / edit link (Ctrl+K)"
          extraClass="term-rt__tool--link"
          onClick={() => setLinkOpen(true)}
        />
        <span className="term-rt__toolbar-sep" aria-hidden="true">│</span>
        <ToolBtn
          label="↶"
          disabled={!editor.can().chain().focus().undo().run()}
          title="Undo (Ctrl+Z)"
          onClick={() => editor.chain().focus().undo().run()}
        />
        <ToolBtn
          label="↷"
          disabled={!editor.can().chain().focus().redo().run()}
          title="Redo (Ctrl+Shift+Z)"
          onClick={() => editor.chain().focus().redo().run()}
        />
      </div>

      {/* ── Link popover (inline under the toolbar) ────────────────── */}
      {linkOpen && (
        <LinkPopover editor={editor} onClose={() => setLinkOpen(false)} />
      )}

      {/* ── Editor body ────────────────────────────────────────────── */}
      <div
        className="term-rt__body-wrap"
        style={{ minHeight: minBodyHeight }}
        onKeyDown={handleEditorKeyDown}
      >
        <EditorContent editor={editor} className="term-rt__body" />
      </div>
    </div>
  );
});

TerminalRichText.displayName = 'TerminalRichText';

export default TerminalRichText;
